import { useState, useRef } from "react";
import QueueView from "./views/QueueView.jsx";
import ProcessingView from "./views/ProcessingView.jsx";
import HistoryView from "./views/HistoryView.jsx";
import NewJobPanel from "./views/NewJobPanel.jsx";
import RecipesPanel from "./views/RecipesPanel.jsx";
import { MOCK_JOBS } from "./lib/mockData.js";

/**
 * Top-level AutoCycle app, mounted from src/App.jsx when activeApp === "autocycle".
 * Nav items (appRegistry.js): Queue, Processing, History.
 *
 * AutoCycle is the suite's photogrammetry-processing control plane: queue a job
 * (pick a recipe + point it at photos), watch it move through the RealityScan →
 * Blender pipeline, review outputs in History. Heavy processing runs on a separate
 * local worker (Phase 3); this is the UI + job queue.
 *
 * PHASE 1: UI + realistic mock data, no backend. State is local — new jobs append
 * to the in-memory list so the flow is fully clickable. Phase 2 swaps MOCK_JOBS for
 * live Firestore via dataService.
 */
export default function AutocycleApp({ activeTab }) {
  const [jobs, setJobs] = useState(MOCK_JOBS);
  const [newJobOpen, setNewJobOpen] = useState(false);
  const [recipesOpen, setRecipesOpen] = useState(false);
  const [seedRecipeId, setSeedRecipeId] = useState("");

  const nextId = useRef(2042);
  const addJob = (draft) => {
    const job = { id: `ac_${nextId.current++}`, status: "queued", created: "just now", ...draft };
    setJobs((prev) => [job, ...prev]);
    setNewJobOpen(false);
    setSeedRecipeId("");
  };

  const cancelJob = (id) => setJobs((prev) => prev.filter((j) => j.id !== id));

  const openNewJob = (recipeId = "") => {
    setSeedRecipeId(recipeId);
    setRecipesOpen(false);
    setNewJobOpen(true);
  };

  const rerun = (job) => {
    openNewJob(job.recipeId);
  };

  // Distinct shoots currently in the list, for the New Job panel's shoot picker.
  const shoots = [];
  const seen = new Set();
  for (const j of jobs) {
    const s = j.shoot;
    if (s && !seen.has(s.id)) { seen.add(s.id); shoots.push(s); }
  }

  return (
    <>
      {activeTab === 1 ? (
        <ProcessingView jobs={jobs} onCancel={cancelJob} />
      ) : activeTab === 2 ? (
        <HistoryView jobs={jobs} onRerun={rerun} />
      ) : (
        <QueueView
          jobs={jobs}
          onNewJob={() => openNewJob()}
          onOpenRecipes={() => setRecipesOpen(true)}
          onCancel={cancelJob}
        />
      )}

      {newJobOpen && (
        <NewJobPanel
          initialRecipeId={seedRecipeId}
          shoots={shoots}
          onClose={() => setNewJobOpen(false)}
          onSubmit={addJob}
        />
      )}
      {recipesOpen && (
        <RecipesPanel onClose={() => setRecipesOpen(false)} onUseRecipe={(id) => openNewJob(id)} />
      )}
    </>
  );
}
